import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Moment} from "moment";

// Die Props, die der Hook selbst steuert; der Rest geht unverändert an den Picker
export type TypedPickerProps = {
    value?: Moment | null;
    defaultValue?: Moment | null;
    open?: boolean;
    disabled?: boolean;
    allowClear?: boolean;
    onChange?: (value: Moment | null, dateString: string) => void;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    onOpenChange?: (open: boolean) => void;
    onSelect?: (value: Moment) => void;
};

type TypedPickerOptions = {
    // Getippter Text als Wert; null, wenn er nicht gilt
    parse: (text: string, current: Moment | null) => Moment | null;
    // Gleich im Sinne des Feldes (Tag oder Minute); dann wird nichts gemeldet
    isSame: (a: Moment, b: Moment) => boolean;
    // Format für den dateString in onChange
    format: string;
};

// Übernimmt Getipptes bei Enter, Tab und Klick daneben und verwirft es bei Escape,
// für antd-DatePicker und -TimePicker. Getipptes parst der Hook selbst: rc-picker
// merkt sich sonst jeden gültigen Zwischenstand („1.1.20“ beim Tippen von
// „1.1.2027“) und übernimmt ihn bei Enter oder Tab.
// Enter (bei Getipptem) und Tab erreichen ein eigenes onKeyDown nicht. Tab führt
// deshalb auch nicht in die Panel-Navigation; das Panel ist per Maus und
// Eintippen bedienbar.
const useTypedPicker = (props: TypedPickerProps, {parse, isSame, format}: TypedPickerOptions) => {
    const {value, defaultValue, open: openProp, disabled, allowClear, onChange, onFocus, onOpenChange, onSelect} = props;
    // Gesteuert, sobald value übergeben wird, auch als undefined: Formulare
    // reichen für ein leeres Feld oft undefined statt null durch
    const isControlled = "value" in props;
    // Ohne value merkt sich die Komponente den Wert selbst
    const [innerValue, setInnerValue] = useState<Moment | null>(defaultValue ?? null);
    const current = isControlled ? value ?? null : innerValue;
    // Panel selbst steuern, damit rc-picker beim Verlassen nichts übernimmt
    const [open, setOpen] = useState(false);
    // Getippter Text seit dem Fokus oder der letzten Übernahme
    const typedRef = useRef<string | null>(null);
    // Im Panel gewählt, aber nicht bestätigt: Zeitfelder übernehmen erst mit „OK“
    const selectedRef = useRef<Moment | null>(null);
    const escapeClosesPopupRef = useRef(false);
    const blurringRef = useRef(false);

    const handleChange = (date: Moment | null, dateString: string) => {
        // Beim Verlassen übernimmt rc-picker seinen letzten Zwischenstand; das
        // Getippte übernimmt stattdessen commitPending
        if (blurringRef.current) {
            return;
        }
        typedRef.current = null;
        selectedRef.current = null;
        // Alle Wege laufen hier durch, auch „Jetzt“ und „OK“ von rc-picker, das
        // Sekunden und Datum mitvergleicht
        if (date && current && isSame(date, current)) {
            return;
        }
        if (!isControlled) {
            setInnerValue(date);
        }
        onChange?.(date, dateString);
    };

    const commitValue = (date: Moment) => handleChange(date, date.format(format));

    // rc-picker schließt beim Klick daneben schon im Mousedown, also vor der
    // Übernahme im Blur. Mit offenem Getipptem schließt deshalb erst der Blur.
    const handlePickerOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && typedRef.current !== null) {
            return;
        }
        setOpen(nextOpen);
    };

    // Erst nach dem Rendern melden (wie rc-picker): Wer beim Schließen Formularwerte
    // liest, sieht sonst noch den Wert vor der Übernahme
    const reportedOpenRef = useRef(open);

    useLayoutEffect(() => {
        if (reportedOpenRef.current !== open) {
            reportedOpenRef.current = open;
            onOpenChange?.(open);
        }
    });

    // Gesperrt oder deaktiviert: Panel auch intern schließen, sonst öffnet es sich
    // beim Entsperren von selbst
    useEffect(() => {
        if (openProp === false || disabled) {
            setOpen(false);
        }
    }, [openProp, disabled]);

    // Übernimmt Getipptes, sonst eine unbestätigte Panel-Auswahl; false, wenn das
    // Getippte nicht gilt
    const commitPending = () => {
        const text = typedRef.current;

        if (text === null) {
            if (selectedRef.current) {
                commitValue(selectedRef.current);
            }

            return true;
        }
        if (text.trim() === "") {
            // Geleertes Feld nur übernehmen, wenn das Feld leer sein darf
            if (allowClear === false) {
                return false;
            }
            typedRef.current = null;
            selectedRef.current = null;
            if (current) {
                handleChange(null, "");
            }

            return true;
        }

        const typed = parse(text, current);

        if (!typed) {
            return false;
        }
        commitValue(typed);

        return true;
    };

    const wrapperProps: React.HTMLAttributes<HTMLSpanElement> = {
        role: "presentation",
        onKeyDownCapture: (event) => {
            // Tab nicht an rc-picker geben: der würde in die Panel-Navigation
            // springen statt ins nächste Feld. Übernommen und geschlossen wird beim
            // Verlassen, damit onOpenChange schon den neuen Wert sieht.
            if (event.key === "Tab") {
                event.stopPropagation();
            }
            if (event.key === "Enter" && typedRef.current !== null) {
                event.stopPropagation();
                event.preventDefault();
                // Ungültiges bleibt stehen, damit man es korrigieren kann
                if (commitPending()) {
                    setOpen(false);
                }
            }
            // Esc bricht die Eingabe ab
            if (event.key === "Escape") {
                typedRef.current = null;
                selectedRef.current = null;
                escapeClosesPopupRef.current = open;
            }
        },
        // Esc, das nur das Panel schließt, soll keinen umgebenden Dialog schließen
        onKeyDown: (event) => {
            if (event.key === "Escape" && escapeClosesPopupRef.current) {
                event.stopPropagation();
            }
            escapeClosesPopupRef.current = false;
        },
        onChange: (event) => {
            typedRef.current = (event.target as HTMLInputElement).value;
        },
        // Nativer Fokusverlust: rc-pickers eigener onBlur fällt nach manchen
        // Klicks ins Feld einmal aus und übernimmt sonst nur Zwischenstände
        onBlurCapture: () => {
            blurringRef.current = true;
        },
        onBlur: () => {
            blurringRef.current = false;
            commitPending();
            typedRef.current = null;
            selectedRef.current = null;
            setOpen(false);
        },
    };

    const pickerProps = {
        value: current,
        // open={false} von außen sperrt das Panel, sonst steuert es der Hook
        open: openProp === false ? false : open,
        onOpenChange: handlePickerOpenChange,
        onChange: handleChange,
        onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
            typedRef.current = null;
            selectedRef.current = null;
            onFocus?.(event);
        },
        onSelect: (date: Moment) => {
            // Die zuletzt bediente Quelle gilt: rc-picker überschreibt den Text,
            // ohne dass ein onChange am Input kommt
            typedRef.current = null;
            selectedRef.current = date;
            onSelect?.(date);
        },
    };

    return {wrapperProps, pickerProps};
};

export default useTypedPicker;
